import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { notesApi } from "./notes";

// イメージボードは共有ノートのページ（display_style: "board"）。note_id で指定する。
// 型定義（BE の app/api_views/image_board.py に基づく）
// 詳細仕様は soremo-soundcheck/docs/api/image-board.md を参照。

export interface ImageBoardItem {
  id: string;
  image_key: string;
  image_url: string;
  /** ワールド座標（無限キャンバス上の位置） */
  x: number;
  y: number;
  width: number;
  height: number;
  /** 重なり順（大きいほど手前） */
  z_index: number;
  created_by: number | null;
  created: string | null;
  modified: string | null;
}

export interface ImageBoardItemsResponse {
  status: string;
  data: { note_id: string; items: ImageBoardItem[] };
}

export interface ProjectMainBoardResponse {
  status: string;
  data: { note_id: string; section_id: string };
}

export interface SingleImageBoardItemResponse {
  status: string;
  data: { item: ImageBoardItem };
}

export interface CreateImageBoardItemParams {
  note_id: string;
  image_key: string;
  x: number;
  y: number;
  width: number;
  height: number;
  z_index?: number;
}

export interface UpdateImageBoardItemParams {
  note_id: string;
  item_id: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  z_index?: number;
}

export interface DeleteImageBoardItemParams {
  note_id: string;
  item_id: string;
}

/**
 * 手描きストロークの 1 点。`[x, y, pressure]`。
 * x / y はワールド座標、pressure は 0〜1（筆圧非対応ポインタは 0.5）。
 */
export type StrokePoint = [number, number, number];

export interface ImageBoardStroke {
  id: string;
  points: StrokePoint[];
  /** 線色（#rrggbb） */
  color: string;
  /** 基準の太さ（ワールド単位）。筆圧で変調して描く */
  width: number;
  z_index: number;
  created_by: number | null;
  created: string | null;
}

export interface ImageBoardStrokesResponse {
  status: string;
  data: { strokes: ImageBoardStroke[] };
}

export interface SingleImageBoardStrokeResponse {
  status: string;
  data: { stroke: ImageBoardStroke };
}

export interface CreateImageBoardStrokeParams {
  note_id: string;
  points: StrokePoint[];
  color: string;
  width: number;
}

export interface DeleteImageBoardStrokesParams {
  note_id: string;
  /** 消しゴム / Undo で消すストローク（1 リクエスト 200 件まで） */
  ids: string[];
}

/**
 * プロジェクトのメインのボード解決（useEnsureProjectMainBoardMutation）の
 * fixedCacheKey。⌥7 のトグルと旧 URL の振り替えで結果を共有する。
 */
export const projectMainBoardCacheKey = (projectId: string | undefined) =>
  `project-main-board:${projectId ?? ""}`;

// タグによる自動 refetch は使わない。全ミューテーションが onQueryStarted で
// getImageBoardItems のキャッシュを直接更新し、他メンバーの変更は
// refetchOnFocus（フォーカス復帰時の再取得）で取り込む。
export const imageBoardApi = createApi({
  reducerPath: "imageBoardApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  refetchOnFocus: true,
  endpoints: (builder) => ({
    getImageBoardItems: builder.query<ImageBoardItemsResponse, { note_id: string }>({
      query: ({ note_id }) => `/api/notes/${note_id}/board/items`,
      keepUnusedDataFor: 300,
    }),

    createImageBoardItem: builder.mutation<SingleImageBoardItemResponse, CreateImageBoardItemParams>({
      query: ({ note_id, ...body }) => ({
        url: `/api/notes/${note_id}/board/items`,
        method: "POST",
        body,
      }),
      // 作成されたアイテムをキャッシュへ直接追加する（全体 refetch を避ける）
      async onQueryStarted({ note_id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            imageBoardApi.util.updateQueryData(
              "getImageBoardItems",
              { note_id },
              (draft) => {
                draft.data.items.push(data.data.item);
              },
            ),
          );
        } catch {
          // 作成失敗時はキャッシュ変更を行わない
        }
      },
    }),

    updateImageBoardItem: builder.mutation<SingleImageBoardItemResponse, UpdateImageBoardItemParams>({
      query: ({ note_id, item_id, ...body }) => ({
        url: `/api/notes/${note_id}/board/items/${item_id}`,
        method: "PATCH",
        body,
      }),
      // ドラッグ/リサイズ確定時の楽観更新（失敗時はロールバック）
      async onQueryStarted(
        { note_id, item_id, ...patch },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          imageBoardApi.util.updateQueryData(
            "getImageBoardItems",
            { note_id },
            (draft) => {
              const item = draft.data.items.find((i) => i.id === item_id);
              if (item) {
                Object.assign(item, patch);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    getImageBoardStrokes: builder.query<ImageBoardStrokesResponse, { note_id: string }>({
      query: ({ note_id }) => `/api/notes/${note_id}/board/strokes`,
      keepUnusedDataFor: 300,
    }),

    createImageBoardStroke: builder.mutation<
      SingleImageBoardStrokeResponse,
      CreateImageBoardStrokeParams
    >({
      query: ({ note_id, ...body }) => ({
        url: `/api/notes/${note_id}/board/strokes`,
        method: "POST",
        body,
      }),
      // 描き終わりのたびに全体を取り直さず、返ってきた 1 本だけ足す。
      async onQueryStarted({ note_id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            imageBoardApi.util.updateQueryData(
              "getImageBoardStrokes",
              { note_id },
              (draft) => {
                draft.data.strokes.push(data.data.stroke);
              },
            ),
          );
        } catch {
          // 作成失敗時はキャッシュ変更を行わない
        }
      },
    }),

    deleteImageBoardStrokes: builder.mutation<
      { status: string; data: { deleted: number } },
      DeleteImageBoardStrokesParams
    >({
      query: ({ note_id, ids }) => ({
        url: `/api/notes/${note_id}/board/strokes`,
        method: "DELETE",
        body: { ids },
      }),
      // 楽観削除（失敗時はロールバック）
      async onQueryStarted({ note_id, ids }, { dispatch, queryFulfilled }) {
        const removing = new Set(ids);
        const patchResult = dispatch(
          imageBoardApi.util.updateQueryData(
            "getImageBoardStrokes",
            { note_id },
            (draft) => {
              draft.data.strokes = draft.data.strokes.filter(
                (s) => !removing.has(s.id),
              );
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // プロジェクトのメインのボード（無ければ BE が作る）。⌥7 /
    // 旧 URL（?tab=imageboard）からノートタブのボードを開くために使う。
    ensureProjectMainBoard: builder.mutation<ProjectMainBoardResponse, { project_id: string }>({
      query: ({ project_id }) => ({
        url: `/api/${project_id}/image-board/main`,
        method: "POST",
      }),
      // 作られたばかりのセクション / ボードをノートのツリーへ反映する
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notesApi.util.invalidateTags([{ type: "NotesTree", id: "TREE" }]));
        } catch {
          // 失敗時は呼び出し側でトーストを出す
        }
      },
    }),

    deleteImageBoardItem: builder.mutation<{ status: string; message: string }, DeleteImageBoardItemParams>({
      query: ({ note_id, item_id }) => ({
        url: `/api/notes/${note_id}/board/items/${item_id}`,
        method: "DELETE",
      }),
      // 楽観削除（失敗時はロールバック）
      async onQueryStarted({ note_id, item_id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          imageBoardApi.util.updateQueryData(
            "getImageBoardItems",
            { note_id },
            (draft) => {
              draft.data.items = draft.data.items.filter((i) => i.id !== item_id);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetImageBoardItemsQuery,
  useCreateImageBoardItemMutation,
  useUpdateImageBoardItemMutation,
  useDeleteImageBoardItemMutation,
  useGetImageBoardStrokesQuery,
  useCreateImageBoardStrokeMutation,
  useDeleteImageBoardStrokesMutation,
  useEnsureProjectMainBoardMutation,
} = imageBoardApi;
